/**
 * UK Tax Year Utilities
 *
 * UK tax year runs from April 6 to April 5 (inclusive)
 * Example: 2024-2025 tax year runs from 6 April 2024 to 5 April 2025
 */

/**
 * Calculate UK tax year from a date
 * UK tax year runs from April 6 to April 5
 *
 * @param date - Date to calculate tax year for
 * @returns Tax year string (e.g., "2024-2025")
 *
 * @example
 * getTaxYearFromDate(new Date('2024-04-05')) // "2023-2024" (before April 6)
 * getTaxYearFromDate(new Date('2024-04-06')) // "2024-2025" (on/after April 6)
 * getTaxYearFromDate(new Date('2024-07-01')) // "2024-2025"
 */
export function getTaxYearFromDate(date: Date): string {
  const year = date.getFullYear();
  const month = date.getMonth(); // 0-indexed (0 = January, 3 = April)
  const day = date.getDate();

  // UK tax year starts April 6
  // Before April 6: previous tax year (year-1 to year)
  // On/after April 6: current tax year (year to year+1)
  const isBeforeNewTaxYear = month < 3 || (month === 3 && day < 6); // April is month 3

  const taxYearStart = isBeforeNewTaxYear ? year - 1 : year;
  const taxYearEnd = taxYearStart + 1;

  return `${taxYearStart}-${taxYearEnd}`;
}

/**
 * Get date range for a UK tax year
 *
 * @param taxYear - Tax year string (e.g., "2024-2025")
 * @returns Start and end dates for the tax year
 *
 * @example
 * getTaxYearDates("2024-2025")
 * // {
 * //   start: Date('2024-04-06T00:00:00.000Z'),
 * //   end: Date('2025-04-05T23:59:59.999Z')
 * // }
 */
export function getTaxYearDates(taxYear: string): { start: Date; end: Date } {
  const [startYear, endYear] = taxYear.split('-').map(Number);

  // Tax year starts April 6 at 00:00:00
  const start = new Date(startYear, 3, 6, 0, 0, 0, 0); // April = month 3 (0-indexed)

  // Tax year ends April 5 at 23:59:59.999 of next year
  const end = new Date(endYear, 3, 5, 23, 59, 59, 999);

  return { start, end };
}

/**
 * Get current UK tax year
 *
 * @returns Current tax year string (e.g., "2024-2025")
 *
 * @example
 * getCurrentTaxYear() // "2024-2025" (if called in July 2024)
 */
export function getCurrentTaxYear(): string {
  return getTaxYearFromDate(new Date());
}

/**
 * Get previous N tax years from a given date
 *
 * @param date - Reference date
 * @param count - Number of previous tax years to return
 * @returns Array of tax year strings, most recent first
 *
 * @example
 * getPreviousTaxYears(new Date('2024-07-01'), 3)
 * // ["2023-2024", "2022-2023", "2021-2022"]
 */
export function getPreviousTaxYears(date: Date, count: number): string[] {
  const currentTaxYear = getTaxYearFromDate(date);
  const [currentStart] = currentTaxYear.split('-').map(Number);

  const taxYears: string[] = [];
  for (let i = 1; i <= count; i++) {
    const startYear = currentStart - i;
    const endYear = startYear + 1;
    taxYears.push(`${startYear}-${endYear}`);
  }

  return taxYears;
}

/**
 * Check if a date falls within a specific tax year
 *
 * @param date - Date to check
 * @param taxYear - Tax year string (e.g., "2024-2025")
 * @returns True if date is within the tax year
 *
 * @example
 * isDateInTaxYear(new Date('2024-07-01'), "2024-2025") // true
 * isDateInTaxYear(new Date('2024-04-05'), "2024-2025") // false (still in 2023-2024)
 */
export function isDateInTaxYear(date: Date, taxYear: string): boolean {
  const { start, end } = getTaxYearDates(taxYear);
  return date >= start && date <= end;
}

/**
 * Calculate tax year from a date using alternative logic
 * This matches the HMRC definition where year is determined by the later year in the tax year period
 *
 * @param collectionDate - The collection date
 * @returns Tax year as integer (e.g., 2024 for 2024-2025 tax year)
 *
 * @example
 * calculateTaxYear(new Date('2024-04-05')) // 2024 (tax year 2023-2024)
 * calculateTaxYear(new Date('2024-04-06')) // 2025 (tax year 2024-2025)
 * calculateTaxYear(new Date('2024-07-01')) // 2025 (tax year 2024-2025)
 */
export function calculateTaxYear(collectionDate: Date): number {
  const year = collectionDate.getFullYear();
  const month = collectionDate.getMonth(); // 0-indexed
  const day = collectionDate.getDate();

  // UK tax year starts April 6
  // Before April 6: current year (end of previous tax year)
  // On/after April 6: next year (end of current tax year)
  const isBeforeNewTaxYear = month < 3 || (month === 3 && day < 6);

  return isBeforeNewTaxYear ? year : year + 1;
}

/**
 * Short display form of a tax year string: "2026-2027" → "2026-27".
 *
 * The long form is the codebase's internal convention (see gasds-cap,
 * gift-aid-totals); the short form is what HMRC's own guidance and every
 * trustee-facing screen uses. Returns the input unchanged if it isn't a
 * recognisable `startYear-endYear` pair, so a bad value degrades to
 * visible text rather than "undefined".
 *
 * @example
 * formatTaxYearShort('2026-2027') // "2026-27"
 */
export function formatTaxYearShort(taxYear: string): string {
  const [start, end] = taxYear.split('-');
  if (!start || !end || end.length < 2) return taxYear;
  return `${start}-${end.slice(-2)}`;
}
